import { ControlledFramelikeElement, createControlledFrame } from '@fullstory/controlled-iframe';
import type { FsId } from '@fullstory/recording-api';
import type { NormalizedTagName } from '@fullstory/rewriters';
import { notAwaited } from '@fullstory/typeutils';
import { uaIsSafari } from '@fullstory/useragent';
import type { DocumentEvents } from '../events';
// eslint-disable-next-line import/no-cycle
import { FsDocument } from './fsdocument';
import { FsNode } from './fsnode';
import type { IFsDocumentContainer } from './ifsdocumentcontainer';

/** Shared behaviors for the vDOM representation of <frame> and <iframe> elements. */
export abstract class FsFramelikeElement extends FsNode implements IFsDocumentContainer {
  protected _contentDoc: FsDocument | undefined = undefined;

  constructor(ownerDoc: FsDocument, tagName: NormalizedTagName, fsid?: FsId) {
    super(ownerDoc, tagName, undefined, fsid);
  }

  isFrame(): this is FsFramelikeElement {
    return true;
  }

  /** @override */
  protected flushAttr(dom: Node, name: string, value: string | null): void {
    if (name === 'src' || name === 'srcdoc' || name === 'sandbox') {
      // Don't allow these attributes to get set or unset. Note that this is
      // different than blocking the attributes in NodeRewriter, because doing
      // that would cause them to be unset. We don't want to change them at all.
      // This is for two reasons:
      //  - Allowing these attributes to change could introduce a security risk.
      //  - Changing 'src' or 'srcdoc' would cause our content document to
      //    become detached from the frame.
      return;
    }

    super.flushAttr(dom, name, value);
  }

  /**
   * Set the document contained by this frame.
   * @param events - The DocumentEvents object describing the new document, or
   * undefined if the frame now contains no document.
   * @returns the new FsDocument contained by this frame, or undefined if the
   * frame is now empty.
   */
  setContentDocument(events: DocumentEvents): FsDocument;
  setContentDocument(events: undefined): undefined;
  setContentDocument(events: DocumentEvents | undefined): FsDocument | undefined;
  setContentDocument(events: DocumentEvents | undefined): FsDocument | undefined {
    if (events === undefined) {
      if (this._contentDoc) {
        this._removeContentDocument();
      }
      return undefined;
    }

    if (!this._contentDoc || (this._contentDoc && !this._contentDoc.path.equals(events.path))) {
      this._contentDoc = new FsDocument({
        ctx: this.ownerDoc.ctx,
        events,
        parent: this.ownerDoc,
        handlers: this.ownerDoc.handlers,
      });
      this.invalidateDomSubtree();
    }

    return this._contentDoc;
  }

  private _removeContentDocument(): void {
    if (this._contentDoc) {
      const contentDoc = this._contentDoc;
      this._contentDoc = undefined;
      contentDoc.detach();
    }

    this.invalidateDomSubtree();
  }

  getContentDocument(): FsDocument | undefined {
    return this._contentDoc;
  }
}

/** The vDOM representation of an HTMLFrameElement. */
export class FsFrameElement extends FsFramelikeElement {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    // TODO: can we change this to iframe?
    super(ownerDoc, 'frame' as NormalizedTagName, fsid);
  }

  protected createNode(doc: Document): Node | null {
    if (!this._contentDoc) {
      const frame = createControlledFrame(this.ownerDoc.ctx, doc, this.ownerDoc.doctype, 'frame');
      this._hideContentOfEmptyFrameIfNeeded(frame);
      return frame;
    }

    return this._contentDoc.createFrame(doc, 'frame');
  }

  /** @override */
  async setExcluded(): Promise<void> {
    super.setExcluded();

    if (this._contentDoc) {
      console.error(
        `An excluded frame contains a content document! This probably indicates ` +
          `a bug in the recording script.`,
      );
      return;
    }

    notAwaited(
      this._hideContentOfEmptyFrameIfNeeded(this.dom as ControlledFramelikeElement | null),
    );
  }

  private async _hideContentOfEmptyFrameIfNeeded(
    frame: ControlledFramelikeElement | null,
  ): Promise<void> {
    if (!this._excluded) {
      return;
    }
    if (!frame) {
      return;
    }

    // On Safari, hide the content of this frame. Safari won't show the excluded
    // element background image for excluded <frame> elements unless we do this.
    // Unfortunately, this has the opposite effect on Firefox, so we can't just
    // do it everywhere.
    if (!uaIsSafari) {
      return;
    }

    await frame.loaded;
    if (!frame.isConnected) {
      return;
    }
    if (!frame.contentDocument) {
      return;
    }

    const doc = frame.contentDocument;
    if (!doc.documentElement) {
      return;
    }
    doc.documentElement.style.display = 'none';
  }
}

/** The vDOM representation of an HTMLIFrameElement. */
export class FsIFrameElement extends FsFramelikeElement {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, 'iframe', fsid);
  }

  protected createNode(doc: Document): Node | null {
    if (!this._contentDoc) {
      return createControlledFrame(this.ownerDoc.ctx, doc, this.ownerDoc.doctype, 'iframe');
    }

    return this._contentDoc.createFrame(doc, 'iframe');
  }
}
